
import React, { useState, useEffect } from 'react';
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce"; // Assuming you have a debounce hook
import { searchCandidates, CandidateSearchResult } from '@/components/search/useCandidateSearch';
import { Skeleton } from '@/components/ui/skeleton';

interface CandidateSearchSelectProps {
  selectedCandidateId: string | null;
  onSelectCandidate: (candidateId: string | null, candidateName?: string) => void;
  className?: string;
}

export function CandidateSearchSelect({ 
  selectedCandidateId, 
  onSelectCandidate,
  className 
}: CandidateSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [searchResults, setSearchResults] = useState<CandidateSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCandidateName, setSelectedCandidateName] = useState<string | null>(null);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2) {
      setIsLoading(true);
      searchCandidates(debouncedSearchQuery)
        .then((results) => {
          setSearchResults(results);
        })
        .catch(error => {
          console.error("Failed to search candidates:", error);
          setSearchResults([]);
        })
        .finally(() => setIsLoading(false));
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchQuery]);
  
  useEffect(() => {
    // If a candidateId is pre-selected but we don't have their name,
    // we might need to fetch it or expect it to be passed in.
    // For simplicity, this example assumes if selectedCandidateId is present,
    // its name might have been set via onSelectCandidate initially or is not immediately needed for display in trigger.
    // If selectedCandidateId is null, clear the name.
    if (!selectedCandidateId) {
      setSelectedCandidateName(null);
    }
  }, [selectedCandidateId]);


  const handleSelect = (candidate: CandidateSearchResult) => {
    onSelectCandidate(candidate.id, candidate.name);
    setSelectedCandidateName(candidate.name);
    setOpen(false);
    setSearchQuery(""); 
  };

  const getDisplayLabel = () => {
    if (selectedCandidateId && selectedCandidateName) {
      return selectedCandidateName;
    }
    return "Select candidate...";
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
        >
          {getDisplayLabel()}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false} > {/* We handle filtering via API */}
          <CommandInput 
            placeholder="Search candidate by name..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {isLoading && <CommandItem disabled><Skeleton className="h-4 w-full my-1" /></CommandItem>}
            {!isLoading && debouncedSearchQuery.length >=2 && searchResults.length === 0 && (
              <CommandEmpty>No candidate found.</CommandEmpty>
            )}
            {!isLoading && searchResults.length > 0 && (
              <CommandGroup>
                {searchResults.map((candidate) => (
                  <CommandItem
                    key={candidate.id}
                    value={candidate.name} // Value for cmdk navigation
                    onSelect={() => handleSelect(candidate)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedCandidateId === candidate.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {candidate.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
             {searchQuery.length > 0 && debouncedSearchQuery.length < 2 && !isLoading && (
                <CommandEmpty>Type at least 2 characters to search.</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
